import React, { Fragment, useCallback, useEffect, useRef, useState, KeyboardEvent, ClipboardEvent } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Sync } from '@material-ui/icons';
import { FirebaseError } from '@firebase/util';
import { formatObscuredPhone, OtpSessionConsumedError } from '@/src/helpers/phoneAuth';
import { support } from '../../config';
import {
  Actions,
  CancelButton,
  Description,
  ErrorText,
  OtpInput,
  OtpRow,
  Overlay,
  Panel,
  ResendButton,
  SecondaryText,
  SubmitButton,
  Title,
} from './OTPDialog.styled';

const OTP_LENGTH = 6;

export interface OTPDialogProps {
  open: boolean;
  /** Used for default SMS copy when `description` is omitted. */
  phoneNumber?: string;
  /** Override the default SMS verification description. */
  description?: React.ReactNode;
  /** When false, hides the resend affordance (e.g. TOTP MFA). Default true. */
  showResend?: boolean;
  onVerified: (otp: string) => Promise<void>;
  onResend?: () => Promise<void>;
  onCancel?: () => void;
  dismissible?: boolean;
}

function getFirebaseOtpErrorMessage(code: string): string | null {
  switch (code) {
    case 'auth/invalid-verification-code':
      return 'Invalid code. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many requests. Please try again later.';
    case 'auth/session-expired':
    case 'auth/user-token-expired':
      return 'Session expired. Please try again.';
    case 'auth/code-expired':
      return 'Code expired. Please request a new code.';
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return 'This email is already associated with another account.';
    case 'auth/provider-already-linked':
      return 'This account is already linked. Please try again.';
    default:
      return null;
  }
}

export default function OTPDialog({
  open,
  phoneNumber = '',
  description,
  showResend = true,
  onVerified,
  onResend,
  onCancel = () => {},
  dismissible = true,
}: OTPDialogProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [codeExpired, setCodeExpired] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const otp = digits.join('');
  const isOtpIncomplete = otp.length < OTP_LENGTH;

  const resetOtp = useCallback(() => {
    setDigits(Array(OTP_LENGTH).fill(''));
    setError(undefined);
    setCodeExpired(false);
    setSubmitDisabled(false);
  }, []);

  useEffect(() => {
    if (open) {
      resetOtp();
      setIsSubmitting(false);
      setResendDisabled(false);
      // Focus first slot after open transition
      const timer = window.setTimeout(() => inputRefs.current[0]?.focus(), 50);
      return () => window.clearTimeout(timer);
    }
  }, [open, resetOtp]);

  const updateDigit = (index: number, value: string) => {
    const next = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const updated = [...prev];
      updated[index] = next;
      return updated;
    });
    setError(undefined);

    if (next && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const updated = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      updated[i] = char;
    });
    setDigits(updated);
    setError(undefined);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleClose = () => {
    if (!dismissible || isSubmitting) return;
    onCancel();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isOtpIncomplete || isSubmitting || submitDisabled) return;

    setIsSubmitting(true);
    setError(undefined);

    try {
      await onVerified(otp);
    } catch (e) {
      if (e instanceof OtpSessionConsumedError) {
        // SMS code was already consumed.
        if (e.isAccountConflict) {
          // Parent closes the dialog and shows login CTA; keep a clear in-dialog fallback.
          setError(e.message);
        } else {
          resetOtp();
          setCodeExpired(true);
          setError(e.message);
        }
      } else {
        const firebaseError = e as FirebaseError;
        if (firebaseError?.code) {
          const message = getFirebaseOtpErrorMessage(firebaseError.code);
          if (firebaseError.code === 'auth/invalid-verification-code') {
            setError(message || 'Invalid code. Please try again.');
            setDigits(Array(OTP_LENGTH).fill(''));
            inputRefs.current[0]?.focus();
          } else if (firebaseError.code === 'auth/too-many-requests') {
            setError(message || 'Too many requests. Please try again later.');
            setSubmitDisabled(true);
          } else if (
            firebaseError.code === 'auth/session-expired' ||
            firebaseError.code === 'auth/user-token-expired'
          ) {
            setError(message || 'Session expired. Please try again.');
            setSubmitDisabled(true);
          } else if (firebaseError.code === 'auth/code-expired') {
            resetOtp();
            setCodeExpired(true);
            setError('Code expired. Please request a new code.');
          } else {
            setError(message || 'There was an error validating your code. Please try again.');
          }
        } else {
          setError(
            `An error has occurred. Please try again in a few minutes. If the problem persists, please call customer service at ${support.phone}.`,
          );
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendClick = async () => {
    if (!onResend) return;

    setResendDisabled(true);
    window.setTimeout(() => setResendDisabled(false), 5000);

    try {
      await onResend();
      resetOtp();
      inputRefs.current[0]?.focus();
    } catch {
      setError('There was an error sending the verification code. Please try again.');
    }
  };

  const resolvedDescription =
    description ??
    (phoneNumber
      ? `We sent a code to ${formatObscuredPhone(phoneNumber)}. This verifies your identity and helps keep your account safe.`
      : 'Enter the verification code to continue.');

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Overlay aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel as={Panel}>
                <Title>Enter the 6-digit code</Title>
                <Description>{resolvedDescription}</Description>

                <form onSubmit={handleSubmit}>
                  <OtpRow role="group" aria-label="One-time passcode">
                    {digits.map((digit, index) => (
                      <OtpInput
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        maxLength={1}
                        value={digit}
                        disabled={isSubmitting || codeExpired}
                        onChange={(e) => updateDigit(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        aria-label={`Digit ${index + 1}`}
                      />
                    ))}
                  </OtpRow>

                  {error ? <ErrorText>{error}</ErrorText> : <div style={{ height: 24, marginTop: 16 }} />}

                  {showResend && onResend ? (
                    <SecondaryText>
                      Didn&apos;t receive a code?{' '}
                      <ResendButton type="button" disabled={resendDisabled || isSubmitting} onClick={handleResendClick}>
                        Resend Code
                      </ResendButton>
                    </SecondaryText>
                  ) : null}
                  <SecondaryText>Having Issues? Please contact customer service at {support.phone}</SecondaryText>

                  <Actions>
                    <CancelButton
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        onCancel();
                      }}
                    >
                      Cancel
                    </CancelButton>
                    <SubmitButton
                      type="submit"
                      disabled={isSubmitting || submitDisabled || isOtpIncomplete || codeExpired}
                    >
                      <span className={isSubmitting ? 'loading' : ''}>Submit</span>
                      {isSubmitting ? <Sync className="spinner" /> : null}
                    </SubmitButton>
                  </Actions>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
